import React, { useEffect, Suspense, lazy } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import ROUTES from "./routesModel";
import { useUser } from "../users/providers/UserProvider";
import Spinner from "../components/Spinner";

// Lazy load all pages for better performance
const LoginPage = lazy(() => import("../users/pages/LoginPage"));
const SingupPage = lazy(() => import("../users/pages/SingupPage"));
const FavCardsPage = lazy(() => import("../cards/pages/FavCardsPage"));
const CardDetailsPage = lazy(() => import("../cards/pages/CardDetailsPage"));
const CategoryCardsPage = lazy(
  () => import("../cards/pages/CategoryCardsPage")
);
const CouponManagementPage = lazy(
  () => import("../coupons/pages/CouponManagementPage")
);
const OrdersManagementPage = lazy(
  () => import("../orders/pages/OrdersManagementPage")
);
const SentOrdersPage = lazy(() => import("../orders/pages/SentOrdersPage"));
const MarketingManagementPage = lazy(
  () => import("../pages/MarketingManagementPage")
);
const CustomerManagementPage = lazy(
  () => import("../pages/CustomerManagementPage")
);
const AnalyticsPage = lazy(() => import("../analytics/pages/AnalyticsPage"));
const CategoryManagementPage = lazy(
  () => import("../cards/pages/CategoryManagementPage")
);
const ProductOptionsManagementPage = lazy(
  () => import("../cards/pages/ProductOptionsManagementPage")
);
const AllProductsPage = lazy(() => import("../cards/pages/AllProductsPage"));
const CreateCardPage = lazy(() => import("../cards/pages/CreateCard"));
const EditCardPage = lazy(() => import("../cards/pages/EditCardPage"));
const EditUserPage = lazy(() => import("../users/pages/EditUserPage"));
const GuestFavoritesPage = lazy(
  () => import("../cards/pages/GuestFavoritesPage")
);
const HomePage = lazy(() => import("../pages/homepage/HomePage"));
const ContactPage = lazy(() => import("../pages/ContactPage"));
const AccessibilityPage = lazy(() => import("../pages/AccessibilityPage"));
const PrivacyPolicyPage = lazy(() => import("../pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("../pages/TermsPage"));
const FAQPage = lazy(() => import("../pages/FAQPage"));
const CancelTransactionPage = lazy(
  () => import("../pages/CancelTransactionPage")
);
const CheckoutPage = lazy(() => import("../pages/CheckoutPage"));
const PaymentPage = lazy(() => import("../pages/PaymentPage"));
const PaymentCallbackPage = lazy(() => import("../pages/PaymentCallbackPage"));
const MathCalculatorPage = lazy(() => import("../pages/MathCalculatorPage"));

// ScrollToTop component to handle automatic scrolling
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// PageViewTracker component to track initial visit (once per session)
const PageViewTracker = () => {
  useEffect(() => {
    // Initialize session tracking only once when component mounts
    // This tracks the initial visit, not every route change
    import("../utils/pageViewTracker").then(({ initializeSessionTracking }) => {
      initializeSessionTracking();
    });
  }, []); // Empty dependency array - only run once on mount

  return null;
};

// Admin Guards with Suspense
const AdminGuard = ({ Component }: { Component: React.ComponentType }) => {
  const { user } = useUser();
  return user?.isAdmin ? (
    <Suspense fallback={<Spinner />}>
      <Component />
    </Suspense>
  ) : (
    <Navigate to={ROUTES.ROOT} />
  );
};

const Router = () => {
  return (
    <>
      <ScrollToTop />
      <PageViewTracker />
      <Routes>
        <Route
          path={ROUTES.ROOT}
          element={
            <Suspense fallback={<Spinner />}>
              <HomePage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.CREATE_CARD}
          element={
            <Suspense fallback={<Spinner />}>
              <CreateCardPage />
            </Suspense>
          }
        />

        <Route
          path={`${ROUTES.CARD_DETATLS}/:productName/:cardId`}
          element={
            <Suspense fallback={<Spinner />}>
              <CardDetailsPage />
            </Suspense>
          }
        />

        {/* Fallback route for backward compatibility */}
        <Route
          path="/card-info/:cardId"
          element={
            <Suspense fallback={<Spinner />}>
              <CardDetailsPage />
            </Suspense>
          }
        />

        <Route
          path={`${ROUTES.EDIT_CARD}/:cardId`}
          element={
            <Suspense fallback={<Spinner />}>
              <EditCardPage />
            </Suspense>
          }
        />

        <Route
          path={`${ROUTES.EDIT_ACCOUNT}/:userId`}
          element={
            <Suspense fallback={<Spinner />}>
              <EditUserPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.LOGIN}
          element={
            <Suspense fallback={<Spinner />}>
              <LoginPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.SINGUP}
          element={
            <Suspense fallback={<Spinner />}>
              <SingupPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.FAV_CARDS}
          element={
            <Suspense fallback={<Spinner />}>
              <FavCardsPage />
            </Suspense>
          }
        />

        <Route
          path={`${ROUTES.CATEGORY_CARDS}/:category`}
          element={
            <Suspense fallback={<Spinner />}>
              <CategoryCardsPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.GUEST_FAVORITES}
          element={
            <Suspense fallback={<Spinner />}>
              <GuestFavoritesPage />
            </Suspense>
          }
        />

        {/* Admin Routes */}
        <Route
          path={ROUTES.ADMIN_COUPONS}
          element={<AdminGuard Component={CouponManagementPage} />}
        />
        <Route
          path="/admin/orders"
          element={<AdminGuard Component={OrdersManagementPage} />}
        />
        <Route
          path={ROUTES.ADMIN_SENT_ORDERS}
          element={<AdminGuard Component={SentOrdersPage} />}
        />
        <Route
          path={ROUTES.ADMIN_MARKETING}
          element={<AdminGuard Component={MarketingManagementPage} />}
        />
        <Route
          path={ROUTES.ADMIN_CUSTOMERS}
          element={<AdminGuard Component={CustomerManagementPage} />}
        />
        <Route
          path={ROUTES.ADMIN_ANALYTICS}
          element={<AdminGuard Component={AnalyticsPage} />}
        />
        <Route
          path={ROUTES.ADMIN_CATEGORIES}
          element={<AdminGuard Component={CategoryManagementPage} />}
        />
        <Route
          path={ROUTES.ADMIN_PRODUCT_OPTIONS}
          element={<AdminGuard Component={ProductOptionsManagementPage} />}
        />
        <Route
          path={ROUTES.ADMIN_ALL_PRODUCTS}
          element={<AdminGuard Component={AllProductsPage} />}
        />

        <Route
          path={ROUTES.CHECKOUT}
          element={
            <Suspense fallback={<Spinner />}>
              <CheckoutPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.PAYMENT}
          element={
            <Suspense fallback={<Spinner />}>
              <PaymentPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.PAYMENT_CALLBACK}
          element={
            <Suspense fallback={<Spinner />}>
              <PaymentCallbackPage />
            </Suspense>
          }
        />

        {/* Footer Pages */}
        <Route
          path={ROUTES.CONTACT}
          element={
            <Suspense fallback={<Spinner />}>
              <ContactPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.ACCESSIBILITY}
          element={
            <Suspense fallback={<Spinner />}>
              <AccessibilityPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.PRIVACY_POLICY}
          element={
            <Suspense fallback={<Spinner />}>
              <PrivacyPolicyPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.TERMS}
          element={
            <Suspense fallback={<Spinner />}>
              <TermsPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.FAQ}
          element={
            <Suspense fallback={<Spinner />}>
              <FAQPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.CANCEL_TRANSACTION}
          element={
            <Suspense fallback={<Spinner />}>
              <CancelTransactionPage />
            </Suspense>
          }
        />

        <Route
          path={ROUTES.MATH}
          element={
            <Suspense fallback={<Spinner />}>
              <MathCalculatorPage />
            </Suspense>
          }
        />

        {/* Sandbox Routes */}
        <Route path={ROUTES.SANDBOX} element={<Navigate to={ROUTES.ROOT} />} />
      </Routes>
    </>
  );
};

export default Router;
